import React, { useEffect, useMemo, useState } from "react";
import { ArrowUpRight, Search } from "lucide-react";
import data from "../../data/projects.json";
import Card, { Kicker } from "../brand/Surface";
import Reveal from "../brand/Reveal";
import Hl from "../brand/Hl";
import { useTilt } from "../../lib/interactive";
import { cn } from "../../lib/utils";

/**
 * Covers are bundled, and each record names its own file.
 *
 * There used to be a hardcoded { FlatFlow: "flatflow", … } map here, so adding
 * a project meant two edits in two files — and forgetting the second one
 * silently produced a card with no image. The filename lives on the record now,
 * where the editor at /beheer can write it.
 */
const covers = require.context("../../images/projectimg", false, /\.webp$/);
const cover = (file) => {
  if (!file) return null;
  try {
    return covers(`./${file}`);
  } catch {
    return null;
  }
};

/** Deterministic 0..1 from a string — same name, same layout, every render. */
function seed(str = "") {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) % 1000) / 1000;
}

/* Read once at module level: the import is static, so recomputing this inside
   the component would give useMemo a new array on every render. */
const PROJECTS = data.projects ?? [];

export default function Projects() {
  const [query, setQuery] = useState("");
  const [tag, setTag] = useState("all");

  useEffect(() => {
    document.title = "Werk · Casper De Bock";
  }, []);

  /* Every tag that actually appears, most common first. */
  const tags = useMemo(() => {
    const count = {};
    for (const p of PROJECTS) for (const t of p.tags ?? []) count[t] = (count[t] ?? 0) + 1;
    return Object.entries(count)
      .sort((a, b) => b[1] - a[1])
      .map(([t]) => t);
  }, []);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return PROJECTS.filter((p) => tag === "all" || (p.tags ?? []).includes(tag))
      .filter((p) => !q || `${p.name} ${p.description}`.toLowerCase().includes(q))
      .sort((a, b) => Number(b.year) - Number(a.year));
  }, [query, tag]);

  return (
    <div>
      <header className="mb-9">
        <Kicker>Het werk</Kicker>
        <h1 className="max-w-[16ch]">
          Wat ik <Hl>gebouwd</Hl> heb.
        </h1>
        <p className="mt-5 max-w-[54ch] text-[15px]">
          Echte sites voor echte mensen — verenigingen, zaken, en een paar dingen die ik voor
          mezelf begon. Bijna alles staat nog online.
        </p>
      </header>

      {/* --------------------------------------------------------- filter */}
      <div className="mb-7 flex flex-col gap-2.5">
        <label className="relative w-full">
          <span className="sr-only">Zoek een project</span>
          <Search
            aria-hidden
            className="pointer-events-none absolute top-1/2 left-4 size-4 -translate-y-1/2 text-muted"
            strokeWidth={1.5}
          />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Zoek op naam of wat het doet…"
            className="glass h-12 w-full rounded-full pr-4 pl-11 outline-none placeholder:text-muted focus-visible:border-accent"
          />
        </label>

        <div className="rail gap-1.5 pb-1">
          <Pill active={tag === "all"} onClick={() => setTag("all")}>
            Alles {PROJECTS.length}
          </Pill>
          {tags.map((t) => (
            <Pill key={t} active={tag === t} onClick={() => setTag(t)}>
              {t}
            </Pill>
          ))}
        </div>
      </div>

      {/* ---------------------------------------------------------- grid */}
      {shown.length === 0 ? (
        <Card pad="lg" className="text-center">
          <h2 className="text-[21px]">Niets gevonden.</h2>
          <p className="mx-auto mt-2 max-w-[36ch]">
            Geen project dat daaraan voldoet. Probeer een andere zoekterm.
          </p>
        </Card>
      ) : (
        <Reveal stagger className="grid gap-4 sm:grid-cols-2 desk:grid-cols-3">
          {shown.map((project, i) => (
            <ProjectCard key={project.id} project={project} index={i} />
          ))}
        </Reveal>
      )}

      <p className="kicker mt-12 px-1">
        {shown.length} van {PROJECTS.length} projecten
      </p>
    </div>
  );
}

/* ================================================================== card */

function ProjectCard({ project, index }) {
  const ref = useTilt({ max: 7, lift: 8 });
  const image = cover(project.cover);
  const s = seed(project.name);
  const s2 = seed(project.name + "x");

  return (
    <a
      ref={ref}
      href={project.url}
      target="_blank"
      rel="noreferrer noopener"
      aria-label={`${project.name} — ${project.description.slice(0, 80)}`}
      className={cn(
        "tilt spot group relative isolate flex min-h-[310px] flex-col overflow-hidden rounded-brand-lg p-6",
        "glass tap transition-colors duration-300 hover:border-edge",
      )}
    >
      {/* The cover sits under a themed .scrim: it is texture behind the words,
          never the subject. Blurred on purpose too — unblurred, the
          screenshot's own headings read as words and fight the project name.
          scale-105 hides the soft edge the blur leaves at the bounds. */}
      {image && (
        <img
          src={image}
          alt=""
          loading="lazy"
          className="absolute inset-0 -z-20 size-full scale-105 object-cover opacity-[0.34] blur-[2px] transition-transform duration-700 group-hover:scale-[1.12]"
        />
      )}

      {image && <span aria-hidden className="scrim -z-20" />}

      {/* One accent blob, placed from the name so a grid of these looks
          composed rather than repeated. */}
      <span
        aria-hidden
        className="pointer-events-none absolute -z-10 size-[200px] rounded-full opacity-40 blur-[60px] [background:radial-gradient(circle,var(--c-accent),transparent_65%)]"
        style={{ left: `${4 + s * 52}%`, top: `${-24 + s2 * 30}%` }}
      />

      <span
        aria-hidden
        className="pointer-events-none absolute -right-3 -bottom-9 -z-10 font-display text-[120px] leading-none font-bold tracking-[-0.08em] opacity-[0.06] select-none"
      >
        {String(index + 1).padStart(2, "0")}
      </span>

      <div className="flex flex-1 flex-col">
        <span className="kicker">{project.year}</span>

        <h2 className="mt-auto font-display text-[30px] leading-none font-medium tracking-[-0.06em] lowercase">
          {project.name}
        </h2>
        <p className="mt-3 line-clamp-3 text-[13.5px]">{project.description}</p>

        <div className="mt-6 flex items-center gap-2 border-t border-hair pt-4">
          <div className="flex min-w-0 flex-wrap gap-2">
            {(project.tags ?? []).slice(0, 2).map((t) => (
              <span key={t} className="kicker">
                {t}
              </span>
            ))}
          </div>
          <span
            aria-hidden
            className="ml-auto grid size-9 shrink-0 place-items-center rounded-full bg-accent text-void transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
          >
            <ArrowUpRight className="size-4" strokeWidth={2.25} />
          </span>
        </div>
      </div>
    </a>
  );
}

function Pill({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "tap kicker shrink-0 rounded-full border px-4 py-2.5 whitespace-nowrap transition-colors duration-200",
        active
          ? "border-transparent bg-accent text-void"
          : "border-line bg-surface-solid text-muted hover:text-fg",
      )}
    >
      {children}
    </button>
  );
}
